import React, { useState, useEffect, useCallback } from 'react';
import useAuthenticatedAxios from "../auth/AuthenticatedAxios";
import styled, { keyframes } from 'styled-components';
import { useToast } from '../Context/ToastContext';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Styled Components
const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0a0d12 0%, #111820 50%, #0d1117 100%);
  padding: 40px;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  color: #FFFFFF;
  padding-bottom: 60px;
  animation: ${fadeIn} 0.4s ease-out;
`;

const Header = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
`;

const HeaderLeft = styled.div``;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #FFFFFF;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #fff 0%, #94a3b8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: rgba(148, 163, 184, 0.8);
  line-height: 1.6;
  max-width: 500px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
    color: #fff;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    }
  ` : props.$success ? `
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    color: #fff;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
    }
  ` : props.$danger ? `
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: #fff;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
    }
  ` : `
    background: rgba(255, 255, 255, 0.05);
    color: #94a3b8;
    border: 1px solid rgba(255, 255, 255, 0.1);
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatsBar = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 16px 24px;
  min-width: 140px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.$color || '#fff'};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: rgba(148, 163, 184, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 13px;
  width: 280px;
  outline: none;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: rgba(148, 163, 184, 0.5);
  }
  
  &:focus {
    border-color: rgba(99, 102, 241, 0.5);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const FilterSelect = styled.select`
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 13px;
  outline: none;
  cursor: pointer;
  
  option {
    background: #1a1f2e;
    color: #fff;
  }
`;

const CategorySection = styled.div`
  margin-bottom: 32px;
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const CategoryTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  text-transform: capitalize;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryBadge = styled.span`
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 12px;
  background: rgba(99, 102, 241, 0.2);
  color: #a5b4fc;
  font-weight: 500;
`;

const CustomBadge = styled.span`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #fff;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CategoryActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SmallButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: #94a3b8;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
  }
`;

const ProgramsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
`;

const ProgramCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid ${props => props.$isCustom ? 'rgba(245, 158, 11, 0.3)' : props.$isActive ? 'rgba(34, 197, 94, 0.3)' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: ${props => props.$isCustom ? 'rgba(245, 158, 11, 0.5)' : props.$isActive ? 'rgba(34, 197, 94, 0.5)' : 'rgba(255, 255, 255, 0.15)'};
  }
`;

const ProgramHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const ProgramNameWrapper = styled.div`
  flex: 1;
  padding-right: 12px;
`;

const ProgramName = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 4px 0;
  line-height: 1.4;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  }
  
  &:checked + span:before {
    transform: translateX(20px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.1);
  transition: 0.3s;
  border-radius: 24px;
  
  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const ProgramMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const MetaBadge = styled.span`
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(148, 163, 184, 0.9);
  
  svg {
    margin-right: 4px;
  }
`;

const ProgramDescription = styled.p`
  font-size: 12px;
  color: rgba(148, 163, 184, 0.7);
  line-height: 1.5;
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PipelineInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const PipelineStatusWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PipelineStatus = styled.span`
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${props => props.$active ? '#4ade80' : 'rgba(148, 163, 184, 0.6)'};
`;

const PipelineDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.$active ? '#22c55e' : 'rgba(148, 163, 184, 0.4)'};
`;

const ProgramActions = styled.div`
  display: flex;
  gap: 6px;
`;

const IconButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
  
  &.edit:hover {
    border-color: rgba(99, 102, 241, 0.5);
    color: #a5b4fc;
  }
  
  &.delete:hover {
    border-color: rgba(239, 68, 68, 0.5);
    color: #f87171;
  }
  
  &.pipeline:hover {
    border-color: rgba(34, 197, 94, 0.5);
    color: #4ade80;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: rgba(148, 163, 184, 0.8);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(148, 163, 184, 0.8);
`;

const SetupCard = styled.div`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  margin-bottom: 32px;
`;

const SetupTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 12px;
`;

const SetupDescription = styled.p`
  font-size: 14px;
  color: rgba(148, 163, 184, 0.9);
  margin-bottom: 24px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1a1f2e 0%, #141821 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #e2e8f0;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(148, 163, 184, 0.5);
  }
  
  &:focus {
    border-color: rgba(99, 102, 241, 0.5);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 14px;
  outline: none;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;
  font-family: inherit;
  
  &::placeholder {
    color: rgba(148, 163, 184, 0.5);
  }
  
  &:focus {
    border-color: rgba(99, 102, 241, 0.5);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 14px;
  outline: none;
  cursor: pointer;
  box-sizing: border-box;
  
  option {
    background: #1a1f2e;
    color: #fff;
  }
  
  &:focus {
    border-color: rgba(99, 102, 241, 0.5);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const FormCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  input {
    width: 18px;
    height: 18px;
    accent-color: #6366f1;
  }
`;

const CheckboxLabel = styled.div`
  flex: 1;
`;

const CheckboxTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #e2e8f0;
`;

const CheckboxDescription = styled.div`
  font-size: 11px;
  color: rgba(148, 163, 184, 0.7);
  margin-top: 2px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #e2e8f0;
  margin: 24px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
`;

const DocumentName = styled.input`
  flex: 1;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 13px;
  outline: none;
  
  &::placeholder {
    color: rgba(148, 163, 184, 0.5);
  }
  
  &:focus {
    border-color: rgba(99, 102, 241, 0.5);
  }
`;

const DocumentCategory = styled.select`
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 12px;
  outline: none;
  cursor: pointer;
  
  option {
    background: #1a1f2e;
    color: #fff;
  }
`;

const RemoveButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  
  &:hover {
    background: rgba(239, 68, 68, 0.3);
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  background: transparent;
  color: #94a3b8;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 8px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(99, 102, 241, 0.5);
    color: #a5b4fc;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

// Confirmation Dialog
const ConfirmDialog = styled.div`
  background: linear-gradient(135deg, #1a1f2e 0%, #141821 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const ConfirmTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 12px 0;
`;

const ConfirmMessage = styled.p`
  font-size: 14px;
  color: rgba(148, 163, 184, 0.9);
  margin: 0 0 24px 0;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`;

// Default categories
const CATEGORY_OPTIONS = [
  'Custom',
  'Temporary',
  'Permanent',
  'Citizenship',
  'Refugee',
  'Business',
  'Family',
  'Work',
  'Study',
  'Other'
];

const DOC_CATEGORIES = [
  { value: 'identity', label: 'Identity' },
  { value: 'travel', label: 'Travel' },
  { value: 'financial', label: 'Financial' },
  { value: 'employment', label: 'Employment' },
  { value: 'education', label: 'Education' },
  { value: 'relationship', label: 'Relationship' },
  { value: 'other', label: 'Other' }
];

// Main Component
const ProgramsPipelinesPage = () => {
  const authenticatedAxios = useAuthenticatedAxios();
  const { showToast } = useToast();
  
  const [programs, setPrograms] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [deletingProgram, setDeletingProgram] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Custom',
    estimatedProcessingTime: '',
    processingOffice: '',
    requiredDocuments: [],
    isActive: true,
    createPipeline: true,
    customNotes: ''
  });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    custom: 0
  });

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'Custom',
      estimatedProcessingTime: '',
      processingOffice: '',
      requiredDocuments: [],
      isActive: true,
      createPipeline: true,
      customNotes: ''
    });
  };

  // Load programs
  const loadPrograms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedAxios.get('/expert-programs');
      
      if (response.data.success) {
        setPrograms(response.data.programs);
        setGrouped(response.data.grouped);
        setStats({
          total: response.data.totalPrograms,
          active: response.data.activeCount,
          inactive: response.data.inactiveCount,
          custom: response.data.customCount || 0
        });
      }
    } catch (error) {
      console.error('Error loading programs:', error);
      showToast('Failed to load programs', 'error');
    } finally {
      setLoading(false);
    }
  }, [authenticatedAxios, showToast]);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  // Toggle program (handles both IRCC and custom)
  const handleToggleProgram = async (program) => {
    try {
      setTogglingId(program.typeId || program._id);
      
      let response;
      if (program.isCustom) {
        response = await authenticatedAxios.put(`/custom-programs/${program._id}/toggle`, {
          isActive: !program.isActive
        });
      } else {
        response = await authenticatedAxios.put(`/expert-programs/${program.typeId}/toggle`, {
          isActive: !program.isActive
        });
      }
      
      if (response.data.success) {
        await loadPrograms();
        showToast(
          `${response.data.program.isActive ? 'Activated' : 'Deactivated'} program`,
          'success'
        );
      }
    } catch (error) {
      console.error('Error toggling program:', error);
      showToast('Failed to toggle program', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  // Initialize programs (first-time setup)
  const handleInitialize = async (activateCommon = true) => {
    try {
      setInitializing(true);
      const response = await authenticatedAxios.post('/expert-programs/initialize', {
        activateCommon
      });
      
      if (response.data.success) {
        showToast(response.data.message, 'success');
        await loadPrograms();
      }
    } catch (error) {
      console.error('Error initializing programs:', error);
      showToast('Failed to initialize programs', 'error');
    } finally {
      setInitializing(false);
    }
  };

  // Quick setup
  const handleQuickSetup = async () => {
    try {
      setInitializing(true);
      const response = await authenticatedAxios.post('/expert-programs/quick-setup');
      
      if (response.data.success) {
        showToast(response.data.message, 'success');
        await loadPrograms();
      }
    } catch (error) {
      console.error('Error in quick setup:', error);
      showToast('Failed to complete quick setup', 'error');
    } finally {
      setInitializing(false);
    }
  };

  // Bulk toggle category
  const handleBulkToggleCategory = async (category, isActive) => {
    const categoryPrograms = grouped[category] || [];
    // Only bulk toggle non-custom programs
    const typeIds = categoryPrograms.filter(p => !p.isCustom).map(p => p.typeId);
    
    if (typeIds.length === 0) return;
    
    try {
      const response = await authenticatedAxios.put('/expert-programs/bulk-toggle', {
        typeIds,
        isActive
      });
      
      if (response.data.success) {
        showToast(response.data.message, 'success');
        await loadPrograms();
      }
    } catch (error) {
      console.error('Error bulk toggling:', error);
      showToast('Failed to update programs', 'error');
    }
  };

  // Create custom program
  const handleCreateProgram = async () => {
    if (!formData.name.trim()) {
      showToast('Program name is required', 'error');
      return;
    }
    
    try {
      setSaving(true);
      const response = await authenticatedAxios.post('/custom-programs', formData);
      
      if (response.data.success) {
        showToast('Program created successfully', 'success');
        setShowCreateModal(false);
        resetForm();
        await loadPrograms();
      }
    } catch (error) {
      console.error('Error creating program:', error);
      showToast(error.response?.data?.error || 'Failed to create program', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Update custom program
  const handleUpdateProgram = async () => {
    if (!formData.name.trim()) {
      showToast('Program name is required', 'error');
      return;
    }
    
    try {
      setSaving(true);
      const response = await authenticatedAxios.put(`/custom-programs/${editingProgram._id}`, formData);
      
      if (response.data.success) {
        showToast('Program updated successfully', 'success');
        setShowEditModal(false);
        setEditingProgram(null);
        resetForm();
        await loadPrograms();
      }
    } catch (error) {
      console.error('Error updating program:', error);
      showToast(error.response?.data?.error || 'Failed to update program', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete custom program
  const handleDeleteProgram = async () => {
    try {
      setSaving(true);
      const response = await authenticatedAxios.delete(`/custom-programs/${deletingProgram._id}?deletePipeline=true`);
      
      if (response.data.success) {
        showToast('Program deleted successfully', 'success');
        setShowDeleteConfirm(false);
        setDeletingProgram(null);
        await loadPrograms();
      }
    } catch (error) {
      console.error('Error deleting program:', error);
      showToast('Failed to delete program', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Create pipeline for custom program
  const handleCreatePipeline = async (program) => {
    try {
      const response = await authenticatedAxios.post(`/custom-programs/${program._id}/create-pipeline`);
      
      if (response.data.success) {
        showToast('Pipeline created successfully', 'success');
        await loadPrograms();
      }
    } catch (error) {
      console.error('Error creating pipeline:', error);
      showToast(error.response?.data?.error || 'Failed to create pipeline', 'error');
    }
  };

  // Open edit modal
  const openEditModal = (program) => {
    setEditingProgram(program);
    setFormData({
      name: program.name || '',
      description: program.description || '',
      category: program.category || 'Custom',
      estimatedProcessingTime: program.estimatedProcessingTime || '',
      processingOffice: program.processingOffice || '',
      requiredDocuments: program.requiredDocuments || [],
      isActive: program.isActive,
      createPipeline: false,
      customNotes: program.customNotes || ''
    });
    setShowEditModal(true);
  };

  // Open delete confirm
  const openDeleteConfirm = (program) => {
    setDeletingProgram(program);
    setShowDeleteConfirm(true);
  };

  // Add document
  const addDocument = () => {
    setFormData(prev => ({
      ...prev,
      requiredDocuments: [
        ...prev.requiredDocuments,
        { docId: `doc-${Date.now()}`, name: '', category: 'other', required: true }
      ]
    }));
  };

  // Update document
  const updateDocument = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      requiredDocuments: prev.requiredDocuments.map((doc, i) => 
        i === index ? { ...doc, [field]: value } : doc
      )
    }));
  };

  // Remove document
  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      requiredDocuments: prev.requiredDocuments.filter((_, i) => i !== index)
    }));
  };

  // Filter programs
  const filteredGrouped = Object.entries(grouped).reduce((acc, [category, progs]) => {
    let filtered = progs;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.typeId?.toLowerCase().includes(query)
      );
    }
    
    // Category filter
    if (categoryFilter !== 'all' && category !== categoryFilter) {
      return acc;
    }
    
    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(p => p.isActive);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(p => !p.isActive);
    } else if (statusFilter === 'custom') {
      filtered = filtered.filter(p => p.isCustom);
    }
    
    if (filtered.length > 0) {
      acc[category] = filtered;
    }
    
    return acc;
  }, {});

  // Get unique categories
  const categories = Object.keys(grouped);

  if (loading) {
    return (
      <PageWrapper>
        <LoadingContainer>
          <LoadingSpinner />
          <div>Loading programs...</div>
        </LoadingContainer>
      </PageWrapper>
    );
  }

  const needsSetup = programs.length === 0;

  return (
    <PageWrapper>
      <Container>
        <Header>
          <HeaderLeft>
            <Title>Programs & Pipelines</Title>
            <Subtitle>
              Manage which immigration programs your firm offers. Activating a program
              automatically creates its corresponding workflow pipeline.
            </Subtitle>
          </HeaderLeft>
          <HeaderActions>
            {!needsSetup && (
              <>
                <ActionButton onClick={() => handleBulkToggleCategory('all', false)}>
                  Deactivate All
                </ActionButton>
                <ActionButton $primary onClick={handleQuickSetup} disabled={initializing}>
                  {initializing ? 'Setting up...' : 'Quick Setup (7 Common)'}
                </ActionButton>
                <ActionButton $success onClick={() => setShowCreateModal(true)}>
                  + Create Program
                </ActionButton>
              </>
            )}
          </HeaderActions>
        </Header>

        {needsSetup ? (
          <SetupCard>
            <SetupTitle>Welcome! Set Up Your Programs</SetupTitle>
            <SetupDescription>
              Initialize your account with all available immigration programs.
              You can choose to activate the 7 most common programs automatically,
              or start with all programs inactive and enable them individually.
            </SetupDescription>
            <HeaderActions style={{ justifyContent: 'center' }}>
              <ActionButton onClick={() => handleInitialize(false)} disabled={initializing}>
                Initialize All (Inactive)
              </ActionButton>
              <ActionButton $primary onClick={() => handleInitialize(true)} disabled={initializing}>
                {initializing ? 'Initializing...' : 'Initialize with Common Programs'}
              </ActionButton>
              <ActionButton $success onClick={() => setShowCreateModal(true)}>
                + Create Custom Program
              </ActionButton>
            </HeaderActions>
          </SetupCard>
        ) : (
          <>
            <StatsBar>
              <StatCard>
                <StatValue>{stats.total}</StatValue>
                <StatLabel>Total Programs</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue $color="#4ade80">{stats.active}</StatValue>
                <StatLabel>Active</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue $color="#94a3b8">{stats.inactive}</StatValue>
                <StatLabel>Inactive</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue $color="#f59e0b">{stats.custom}</StatValue>
                <StatLabel>Custom</StatLabel>
              </StatCard>
            </StatsBar>

            <FiltersBar>
              <SearchInput
                type="text"
                placeholder="Search programs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <FilterSelect
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </FilterSelect>
              <FilterSelect
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
                <option value="custom">Custom Only</option>
              </FilterSelect>
            </FiltersBar>

            {Object.entries(filteredGrouped).map(([category, categoryPrograms]) => (
              <CategorySection key={category}>
                <CategoryHeader>
                  <CategoryTitle>
                    {category}
                    <CategoryBadge>
                      {categoryPrograms.filter(p => p.isActive).length}/{categoryPrograms.length}
                    </CategoryBadge>
                  </CategoryTitle>
                  <CategoryActions>
                    <SmallButton onClick={() => handleBulkToggleCategory(category, true)}>
                      Activate All
                    </SmallButton>
                    <SmallButton onClick={() => handleBulkToggleCategory(category, false)}>
                      Deactivate All
                    </SmallButton>
                  </CategoryActions>
                </CategoryHeader>
                
                <ProgramsGrid>
                  {categoryPrograms.map(program => (
                    <ProgramCard 
                      key={program.typeId || program._id} 
                      $isActive={program.isActive}
                      $isCustom={program.isCustom}
                    >
                      <ProgramHeader>
                        <ProgramNameWrapper>
                          <ProgramName>
                            {program.name}
                          </ProgramName>
                          {program.isCustom && <CustomBadge>Custom</CustomBadge>}
                        </ProgramNameWrapper>
                        <ToggleSwitch $disabled={togglingId === (program.typeId || program._id)}>
                          <ToggleInput
                            type="checkbox"
                            checked={program.isActive}
                            onChange={() => handleToggleProgram(program)}
                            disabled={togglingId === (program.typeId || program._id)}
                          />
                          <ToggleSlider />
                        </ToggleSwitch>
                      </ProgramHeader>
                      
                      <ProgramMeta>
                        {program.formsCount > 0 && (
                          <MetaBadge>{program.formsCount} forms</MetaBadge>
                        )}
                        {program.docsCount > 0 && (
                          <MetaBadge>{program.docsCount} docs</MetaBadge>
                        )}
                        {program.estimatedProcessingTime && (
                          <MetaBadge>{program.estimatedProcessingTime}</MetaBadge>
                        )}
                      </ProgramMeta>
                      
                      {program.description && (
                        <ProgramDescription>{program.description}</ProgramDescription>
                      )}
                      
                      <PipelineInfo>
                        <PipelineStatusWrapper>
                          {program.pipeline ? (
                            <>
                              <PipelineDot $active={program.pipeline.status === 'active'} />
                              <PipelineStatus $active={program.pipeline.status === 'active'}>
                                Pipeline: {program.pipeline.milestonesCount} milestones, {program.pipeline.stepsCount} steps
                              </PipelineStatus>
                            </>
                          ) : program.hasPipelineTemplate ? (
                            <>
                              <PipelineDot $active={false} />
                              <PipelineStatus>Pipeline will be created on activation</PipelineStatus>
                            </>
                          ) : (
                            <>
                              <PipelineDot $active={false} />
                              <PipelineStatus>No pipeline</PipelineStatus>
                            </>
                          )}
                        </PipelineStatusWrapper>
                        
                        {program.isCustom && (
                          <ProgramActions>
                            {!program.pipeline && (
                              <IconButton 
                                className="pipeline" 
                                onClick={() => handleCreatePipeline(program)}
                                title="Create Pipeline"
                              >
                                +
                              </IconButton>
                            )}
                            <IconButton 
                              className="edit" 
                              onClick={() => openEditModal(program)}
                              title="Edit Program"
                            >
                              ✎
                            </IconButton>
                            <IconButton 
                              className="delete" 
                              onClick={() => openDeleteConfirm(program)}
                              title="Delete Program"
                            >
                              ×
                            </IconButton>
                          </ProgramActions>
                        )}
                      </PipelineInfo>
                    </ProgramCard>
                  ))}
                </ProgramsGrid>
              </CategorySection>
            ))}

            {Object.keys(filteredGrouped).length === 0 && (
              <EmptyState>
                <div>No programs found matching your filters.</div>
              </EmptyState>
            )}
          </>
        )}
      </Container>

      {/* Create Program Modal */}
      {showCreateModal && (
        <ModalOverlay onClick={() => setShowCreateModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Create Custom Program</ModalTitle>
              <CloseButton onClick={() => setShowCreateModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>Program Name *</FormLabel>
                <FormInput
                  type="text"
                  placeholder="e.g., Business Investor Program"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>Description</FormLabel>
                <FormTextarea
                  placeholder="Brief description of the program..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </FormGroup>
              
              <FormRow>
                <FormGroup>
                  <FormLabel>Category</FormLabel>
                  <FormSelect
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  >
                    {CATEGORY_OPTIONS.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </FormSelect>
                </FormGroup>
                
                <FormGroup>
                  <FormLabel>Est. Processing Time</FormLabel>
                  <FormInput
                    type="text"
                    placeholder="e.g., 6-12 months"
                    value={formData.estimatedProcessingTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimatedProcessingTime: e.target.value }))}
                  />
                </FormGroup>
              </FormRow>
              
              <FormGroup>
                <FormLabel>Processing Office</FormLabel>
                <FormInput
                  type="text"
                  placeholder="e.g., CIC Ottawa"
                  value={formData.processingOffice}
                  onChange={(e) => setFormData(prev => ({ ...prev, processingOffice: e.target.value }))}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>Notes</FormLabel>
                <FormTextarea
                  placeholder="Internal notes about this program..."
                  value={formData.customNotes}
                  onChange={(e) => setFormData(prev => ({ ...prev, customNotes: e.target.value }))}
                />
              </FormGroup>
              
              <SectionTitle>Required Documents</SectionTitle>
              <DocumentList>
                {formData.requiredDocuments.map((doc, index) => (
                  <DocumentItem key={doc.docId}>
                    <DocumentName
                      type="text"
                      placeholder="Document name..."
                      value={doc.name}
                      onChange={(e) => updateDocument(index, 'name', e.target.value)}
                    />
                    <DocumentCategory
                      value={doc.category}
                      onChange={(e) => updateDocument(index, 'category', e.target.value)}
                    >
                      {DOC_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </DocumentCategory>
                    <RemoveButton onClick={() => removeDocument(index)}>×</RemoveButton>
                  </DocumentItem>
                ))}
                <AddButton onClick={addDocument}>
                  + Add Document
                </AddButton>
              </DocumentList>
              
              <SectionTitle>Options</SectionTitle>
              <FormCheckbox>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                <CheckboxLabel>
                  <CheckboxTitle>Activate program immediately</CheckboxTitle>
                  <CheckboxDescription>Make this program available for case assignment</CheckboxDescription>
                </CheckboxLabel>
              </FormCheckbox>
              
              <FormCheckbox style={{ marginTop: '12px' }}>
                <input
                  type="checkbox"
                  checked={formData.createPipeline}
                  onChange={(e) => setFormData(prev => ({ ...prev, createPipeline: e.target.checked }))}
                />
                <CheckboxLabel>
                  <CheckboxTitle>Create default pipeline</CheckboxTitle>
                  <CheckboxDescription>Auto-create a workflow pipeline with standard milestones</CheckboxDescription>
                </CheckboxLabel>
              </FormCheckbox>
            </ModalBody>
            <ModalFooter>
              <ActionButton onClick={() => setShowCreateModal(false)}>
                Cancel
              </ActionButton>
              <ActionButton $primary onClick={handleCreateProgram} disabled={saving}>
                {saving ? 'Creating...' : 'Create Program'}
              </ActionButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Edit Program Modal */}
      {showEditModal && editingProgram && (
        <ModalOverlay onClick={() => setShowEditModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Edit Program</ModalTitle>
              <CloseButton onClick={() => setShowEditModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>Program Name *</FormLabel>
                <FormInput
                  type="text"
                  placeholder="e.g., Business Investor Program"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>Description</FormLabel>
                <FormTextarea
                  placeholder="Brief description of the program..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </FormGroup>
              
              <FormRow>
                <FormGroup>
                  <FormLabel>Category</FormLabel>
                  <FormSelect
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  >
                    {CATEGORY_OPTIONS.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </FormSelect>
                </FormGroup>
                
                <FormGroup>
                  <FormLabel>Est. Processing Time</FormLabel>
                  <FormInput
                    type="text"
                    placeholder="e.g., 6-12 months"
                    value={formData.estimatedProcessingTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimatedProcessingTime: e.target.value }))}
                  />
                </FormGroup>
              </FormRow>
              
              <FormGroup>
                <FormLabel>Processing Office</FormLabel>
                <FormInput
                  type="text"
                  placeholder="e.g., CIC Ottawa"
                  value={formData.processingOffice}
                  onChange={(e) => setFormData(prev => ({ ...prev, processingOffice: e.target.value }))}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>Notes</FormLabel>
                <FormTextarea
                  placeholder="Internal notes about this program..."
                  value={formData.customNotes}
                  onChange={(e) => setFormData(prev => ({ ...prev, customNotes: e.target.value }))}
                />
              </FormGroup>
              
              <SectionTitle>Required Documents</SectionTitle>
              <DocumentList>
                {formData.requiredDocuments.map((doc, index) => (
                  <DocumentItem key={doc.docId || index}>
                    <DocumentName
                      type="text"
                      placeholder="Document name..."
                      value={doc.name}
                      onChange={(e) => updateDocument(index, 'name', e.target.value)}
                    />
                    <DocumentCategory
                      value={doc.category}
                      onChange={(e) => updateDocument(index, 'category', e.target.value)}
                    >
                      {DOC_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </DocumentCategory>
                    <RemoveButton onClick={() => removeDocument(index)}>×</RemoveButton>
                  </DocumentItem>
                ))}
                <AddButton onClick={addDocument}>
                  + Add Document
                </AddButton>
              </DocumentList>
              
              <SectionTitle>Options</SectionTitle>
              <FormCheckbox>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                <CheckboxLabel>
                  <CheckboxTitle>Program is active</CheckboxTitle>
                  <CheckboxDescription>Make this program available for case assignment</CheckboxDescription>
                </CheckboxLabel>
              </FormCheckbox>
            </ModalBody>
            <ModalFooter>
              <ActionButton onClick={() => setShowEditModal(false)}>
                Cancel
              </ActionButton>
              <ActionButton $primary onClick={handleUpdateProgram} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </ActionButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && deletingProgram && (
        <ModalOverlay onClick={() => setShowDeleteConfirm(false)}>
          <ConfirmDialog onClick={e => e.stopPropagation()}>
            <ConfirmTitle>Delete Program?</ConfirmTitle>
            <ConfirmMessage>
              Are you sure you want to delete "{deletingProgram.name}"? 
              This will also delete the associated pipeline. This action cannot be undone.
            </ConfirmMessage>
            <ConfirmActions>
              <ActionButton onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </ActionButton>
              <ActionButton $danger onClick={handleDeleteProgram} disabled={saving}>
                {saving ? 'Deleting...' : 'Delete Program'}
              </ActionButton>
            </ConfirmActions>
          </ConfirmDialog>
        </ModalOverlay>
      )}
    </PageWrapper>
  );
};

export default ProgramsPipelinesPage;
